<template>
  <div
    v-click-outside="closePanel"
    class="bi-period-select"
  >
    <span class="bi-period-select__icon">
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M3.5 5h17l-6.6 7.7v5.6l-3.8 2.3v-7.9L3.5 5z"
          stroke="#7a7a7a"
          stroke-width="1.4"
          stroke-linejoin="round"
        />
      </svg>
    </span>

    <span class="bi-period-select__label">Período de Análise:</span>

    <div class="bi-period-select__field">
      <button
        type="button"
        class="bi-period-select__trigger"
        :class="{ 'is-open': panelOpen }"
        @click="togglePanel"
      >
        <span class="bi-period-select__value">{{ formattedValue }}</span>
        <span class="bi-period-select__caret" />
      </button>

      <div
        v-if="panelOpen"
        class="bi-period-select__panel"
      >
        <div class="bi-period-select__panel-header">
          <button
            type="button"
            class="bi-period-select__nav"
            :disabled="!canNavigateYear(-1)"
            @click="changeYear(-1)"
          >
            &lsaquo;
          </button>
          <span class="bi-period-select__year">{{ displayedYear }}</span>
          <button
            type="button"
            class="bi-period-select__nav"
            :disabled="!canNavigateYear(1)"
            @click="changeYear(1)"
          >
            &rsaquo;
          </button>
        </div>

        <div class="bi-period-select__grid">
          <button
            v-for="(month, index) in abbreviatedMonths"
            :key="month"
            type="button"
            class="bi-period-select__month"
            :class="{ 'is-selected': isSelected(index) }"
            :disabled="isDisabled(index)"
            @click="selectMonth(index)"
          >
            {{ month }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

const MONTH_NAMES_PT = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
]

const MONTH_ABBREVIATIONS_PT = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
]

const MIN_ANALYSIS_PERIOD = '2026-05'

export default {
  name: 'BIPeriodSelect',
  directives: {
    clickOutside: {
      bind(el, binding) {
        el.clickOutsideHandler = event => {
          if (!el.contains(event.target)) binding.value(event)
        }
        document.addEventListener('click', el.clickOutsideHandler)
      },
      unbind(el) {
        document.removeEventListener('click', el.clickOutsideHandler)
      },
    },
  },
  props: {
    value: {
      type: String,
      default: () => moment().format('YYYY-MM'),
    },
  },
  data() {
    const initial = this.value || moment().format('YYYY-MM')
    return {
      abbreviatedMonths: MONTH_ABBREVIATIONS_PT,
      panelOpen: false,
      displayedYear: moment(initial, 'YYYY-MM').year(),
    }
  },
  computed: {
    selectedDate() {
      return moment(this.value || moment().format('YYYY-MM'), 'YYYY-MM')
    },
    formattedValue() {
      return `${MONTH_NAMES_PT[this.selectedDate.month()]}/${this.selectedDate.year()}`
    },
    upperLimit() {
      return moment().startOf('month')
    },
    lowerLimit() {
      return moment(MIN_ANALYSIS_PERIOD, 'YYYY-MM').startOf('month')
    },
  },
  watch: {
    value(val) {
      if (val) this.displayedYear = moment(val, 'YYYY-MM').year()
    },
  },
  methods: {
    togglePanel() {
      this.panelOpen = !this.panelOpen
      if (this.panelOpen) this.displayedYear = this.selectedDate.year()
    },
    closePanel() {
      this.panelOpen = false
    },
    changeYear(delta) {
      if (this.canNavigateYear(delta)) this.displayedYear += delta
    },
    canNavigateYear(delta) {
      const year = this.displayedYear + delta
      return year >= this.lowerLimit.year() && year <= this.upperLimit.year()
    },
    isSelected(index) {
      return (
        this.selectedDate.year() === this.displayedYear &&
        this.selectedDate.month() === index
      )
    },
    isDisabled(index) {
      const month = moment({ year: this.displayedYear, month: index, day: 1 })
      return month.isAfter(this.upperLimit) || month.isBefore(this.lowerLimit)
    },
    selectMonth(index) {
      if (this.isDisabled(index)) return
      this.$emit(
        'input',
        moment({ year: this.displayedYear, month: index, day: 1 }).format('YYYY-MM')
      )
      this.closePanel()
    },
  },
}
</script>

<style scoped lang="scss">
$bi-primary: #7f1a54;

.bi-period-select {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'Exo 2', sans-serif;

  &__icon {
    display: flex;
    align-items: center;
  }

  &__label {
    font-size: 14px;
    color: rgba(90, 90, 90, 1);
    white-space: nowrap;
    font-family: 'Roboto', sans-serif;
  }

  &__field {
    position: relative;
  }

  &__trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    min-width: 220px;
    height: 44px;
    padding: 0 6px 0 16px;
    border: 1px solid #d5d5d5;
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
    outline: none;

    &:hover,
    &.is-open {
      border-color: $bi-primary;
    }
  }

  &__value {
    font-size: 13.33px;
    color: rgba(153, 153, 153, 1);
    font-family: 'Exo 2';
    font-weight: 500;
  }

  &__caret {
    width: 28px;
    height: 28px;
    background: url("data:image/svg+xml,%3Csvg width='28' height='28' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8.33031 11.399L11.2159 14.2752C11.6504 14.7083 12.3523 14.7083 12.7868 14.2752L15.6724 11.399C16.3743 10.6993 15.873 9.5 14.8814 9.5H9.1102C8.11863 9.5 7.62841 10.6993 8.33031 11.399Z' fill='%237F1A54'/%3E%3C/svg%3E")
      center / 28px 28px no-repeat;
    flex-shrink: 0;
  }

  &__panel {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    z-index: 10;
    width: 260px;
    padding: 12px;
    background: #fff;
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }

  &__panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  &__year {
    font-size: 15px;
    font-weight: 600;
    color: #575757;
  }

  &__nav {
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: $bi-primary;
    font-size: 20px;
    line-height: 1;
    cursor: pointer;
    outline: none;

    &:hover:not(:disabled) {
      background: rgba(127, 26, 84, 0.08);
    }

    &:disabled {
      color: #c9c9c9;
      cursor: default;
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
  }

  &__month {
    height: 34px;
    border: none;
    border-radius: 6px;
    background: transparent;
    font-size: 14px;
    font-family: 'Exo 2', sans-serif;
    color: #575757;
    cursor: pointer;
    outline: none;

    &:hover:not(:disabled) {
      background: rgba(127, 26, 84, 0.08);
    }

    &.is-selected {
      background: $bi-primary;
      color: #fff;
    }

    &:disabled {
      color: #c9c9c9;
      cursor: default;
    }
  }
}
</style>
